import React, { MouseEventHandler, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { buttonRowClasses } from 'styles/tailwindClassnames';

type ModalShellProps = {
  title: string;
  subtitle?: string;
  onClose: MouseEventHandler | (() => void);
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Tailwind max-width class for the modal card. Defaults to a standard dialog width. */
  maxWidthClass?: string;
};

const ModalShell = ({
  title,
  subtitle,
  onClose,
  children,
  footer,
  maxWidthClass = 'max-w-2xl',
}: ModalShellProps) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  // The mount effect below runs once, so its Escape handler must read the
  // CURRENT onClose — callers guard it with in-flight state (e.g. a delete
  // confirmation that must not vanish mid-request), and a mount-time closure
  // would bypass that guard.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    // Remember what had focus before the modal opened, so we can restore it on close.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const firstInput = contentRef.current?.querySelector<HTMLElement>(
      'input, select, textarea',
    );
    if (firstInput) {
      firstInput.focus();
    } else {
      const firstButton = contentRef.current?.querySelector<HTMLElement>(
        'button:not([data-modal-close])',
      );
      firstButton?.focus();
    }

    const FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e: KeyboardEvent) => {
      // Close on Escape, mirroring the overlay/X buttons.
      if (e.key === 'Escape') {
        e.stopPropagation();
        (onCloseRef.current as () => void)();
        return;
      }
      // Trap Tab focus inside the modal so the close (X) and all controls stay reachable
      // and focus can't leak to the page behind the overlay.
      if (e.key === 'Tab' && contentRef.current) {
        const focusables = Array.from(
          contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        const inside = contentRef.current.contains(active);
        if (e.shiftKey && (active === first || !inside)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !inside)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Return focus to the trigger so keyboard users don't lose their place.
      previouslyFocused?.focus?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Portaled to <body>: a caller inside a transformed container (the sliding side
  // panels) would otherwise become the containing block for position:fixed, so the
  // modal would center inside the panel and clip at its edge. z-[70] outranks the
  // panels (z-60) and the help panel (z-58..60).
  return createPortal(
    <div className="fixed inset-0 z-[70]">
      <div
        className="fixed inset-0 bg-greyscale-3 opacity-80 transition-colors duration-300"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-10 flex items-center justify-center overflow-y-auto p-4">
        <div
          className={`flex w-full flex-col bg-white ${maxWidthClass} rounded-lg shadow transition-colors duration-300 sm:min-w-[600px]`}
          ref={contentRef}
        >
          <div className="flex justify-end p-3">
            <button
              type="button"
              onClick={onClose}
              data-modal-close
              className="inline-flex items-center rounded-lg bg-transparent p-1.5 text-body-sm text-ui-text-secondary-text transition-colors duration-300 hover:bg-ui-divider-lines hover:text-ui-text-primary-text"
            >
              <svg
                aria-hidden="true"
                className="h-5 w-5 text-ui-text-primary-text transition-colors duration-300"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">{t('Close Modal')}</span>
            </button>
          </div>
          <div className="px-6 pb-6 lg:px-8">
            <h2 className="text-heading-md font-bold text-ui-text-primary-text">
              {title}
              {subtitle && (
                <span className="text-ui-text-primary-text"> — {subtitle}</span>
              )}
            </h2>
            <div className="mt-5">{children}</div>
            {footer && (
              <div
                className={`mt-10 ${buttonRowClasses.rowReverse}`}
                data-testid="ModalShell-footer"
              >
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ModalShell;
